import { AArrowUp, Ear, ZapOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import SelectMenu from '@/components/ui/select-menu';

/**
 * Accessibility review for Preview — the two TRANSFORM tools.
 *
 * A teacher reviewing a task is auditing content THEY authored, not the app's chrome. That is the
 * test every tool here has to pass: does it surface something the teacher both caused and can fix?
 * Colour choices in a rich-text field and labels too long to survive being enlarged both qualify.
 *
 * These two are transforms rather than reports: they change how the task renders and the teacher
 * watches the task itself, so they need a toggle and no space of their own. That is why they work
 * unchanged on phone, where a findings list would need a full-screen portal and lose the
 * side-by-side reading that makes a report useful.
 *
 * DELIBERATELY NOT a substitute for a contrast check. A simulation shows a teacher something they
 * cannot interpret — most people looking at a deuteranopia view conclude "looks fine to me". The
 * measurement ("this text is 2.8:1, below 4.5:1") is the tool that answers the question; this one
 * confirms it afterwards, and catches the class of fault where colour is the ONLY signal.
 */

export type VisionMode = 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia' | 'achromatopsia';

/**
 * Ordered most to least common, not by cone type.
 *
 * The L/M/S convention (protan, deutan, tritan) is what a vision scientist expects; a teacher
 * scanning this menu wants the one most of their students might have at the top. Approximate
 * prevalence in males (these are X-linked, so far rarer in females — tritanopia and achromatopsia
 * are not, and affect both about equally):
 *
 *   deuteranopia   ~1%          protanopia  ~1%, marginally rarer
 *   tritanopia     ~1 in 10,000 achromatopsia ~1 in 30,000
 *
 * NOT simulated: deuteranomaly, an ANOMALOUS (partial) green deficiency and much the commonest
 * form at roughly 5% of males. Full dichromacy is the stricter test — anything legible without a
 * green cone is legible with a weak one — so the omission is deliberate, not an oversight.
 */
export const VISION_MODES: VisionMode[] = ['none', 'deuteranopia', 'protanopia', 'tritanopia', 'achromatopsia'];

/** Applied to the task content only — never to the bar holding these controls. Simulating the
 * app's own chrome is noise a teacher cannot act on, and a tinted control panel makes the mode
 * itself harder to read. */
export function visionFilterStyle(mode: VisionMode, largeText: boolean): React.CSSProperties {
  const style: React.CSSProperties = {};
  if (mode !== 'none') style.filter = `url(#a11y-${mode})`;
  // `zoom`, not `transform: scale`. A transform does not reflow, so it magnifies the same layout
  // and hides the very failure being looked for; zoom re-lays-out inside the same box, which is
  // what makes a seven-point Scale or a long FITB passage visibly break. Closer to page zoom than
  // to text-only zoom (Tailwind sizes from the root rem, which a container cannot override), and
  // page zoom is the stricter of the two to survive.
  if (largeText) style.zoom = 2;
  return style;
}

/**
 * The colour matrices, mounted once while Preview is open.
 *
 * Machado/Oliveira/Fernandes-style approximations of the three dichromacies plus full
 * achromatopsia. They are approximations on purpose: the point is to reveal "these two states are
 * now indistinguishable", which survives the imprecision, not to reproduce anyone's vision exactly.
 */
export function PreviewA11yFilters() {
  return (
    <svg aria-hidden='true' focusable='false' className='absolute h-0 w-0 overflow-hidden'>
      <defs>
        <filter id='a11y-protanopia'>
          <feColorMatrix type='matrix' values='0.567 0.433 0 0 0  0.558 0.442 0 0 0  0 0.242 0.758 0 0  0 0 0 1 0' />
        </filter>
        <filter id='a11y-deuteranopia'>
          <feColorMatrix type='matrix' values='0.625 0.375 0 0 0  0.70 0.30 0 0 0  0 0.30 0.70 0 0  0 0 0 1 0' />
        </filter>
        <filter id='a11y-tritanopia'>
          <feColorMatrix type='matrix' values='0.95 0.05 0 0 0  0 0.433 0.567 0 0  0 0.475 0.525 0 0  0 0 0 1 0' />
        </filter>
        <filter id='a11y-achromatopsia'>
          <feColorMatrix type='matrix' values='0.299 0.587 0.114 0 0  0.299 0.587 0.114 0 0  0.299 0.587 0.114 0 0  0 0 0 1 0' />
        </filter>
      </defs>
    </svg>
  );
}

/**
 * Written out one literal key per mode rather than built as `a11yVision_${mode}`.
 *
 * The locale invariant resolves LITERAL `t('a.b.c')` keys only; a template key is invisible to it,
 * and routing through a `Record<VisionMode, string>` would be invisible to that check AND to the
 * dynamic-key ceiling — the worst of both. Five lines buys real verification.
 */
function visionLabel(mode: VisionMode, t: (k: string) => string): string {
  switch (mode) {
    case 'protanopia':     return t('task.bottomBar.a11yVision_protanopia');
    case 'deuteranopia':   return t('task.bottomBar.a11yVision_deuteranopia');
    case 'tritanopia':     return t('task.bottomBar.a11yVision_tritanopia');
    case 'achromatopsia':  return t('task.bottomBar.a11yVision_achromatopsia');
    case 'none':           return t('task.bottomBar.a11yVision_none');
  }
}

/**
 * The controls, sat beside Exit Preview.
 *
 * Both read as INSPECTION MODES, not settings: an active mode is visibly on, and a teacher must
 * never be left wondering whether they changed what students will see. Hence the active styling and
 * the fact that both reset on leaving Preview.
 */
/**
 * Just the condition's NAME, for the trigger — the parenthetical gloss stays in the open list.
 *
 * Split rather than five more locale keys: both locales write these as "Name (gloss)", and the
 * fallback returns the whole string if a translation ever drops the parenthesis. Getting this
 * wrong is visible immediately (a truncated word), not silent.
 */
function visionName(mode: VisionMode, t: (k: string) => string): string {
  const full = visionLabel(mode, t);
  const cut = full.indexOf(' (');
  return cut > 0 ? full.slice(0, cut) : full;
}

/**
 * Which inspection panel is open, or none.
 *
 * ONE VALUE, not a flag each: there is a single panel slot in Preview on every surface — the one
 * Variables and Flow vacate — so two panels cannot be open at once and a shape that could express
 * it would need a rule somewhere to forbid it.
 */
export type PreviewPanel = 'reading' | 'debug' | null;

export function PreviewA11yControls({ vision, onVision, largeText, onLargeText, reducedMotion, onReducedMotion, panel, onPanel, compact }: {
  vision: VisionMode;
  onVision: (v: VisionMode) => void;
  largeText: boolean;
  onLargeText: (v: boolean) => void;
  /**
   * Simulate a viewer who has asked their device for less motion.
   *
   * A TOGGLE beside Large text, not an entry in the Color vision menu. That menu is a single
   * select of mutually exclusive conditions, and reduced motion is none of those things: it is
   * orthogonal (a student may need it AND deuteranopia at once) and it is not about colour, so
   * inside a control labelled "Color vision" it would be both wrong and unreachable in
   * combination. Large text already sits here for exactly the same reason.
   */
  reducedMotion: boolean;
  onReducedMotion: (v: boolean) => void;
  /**
   * Whether the read-through is open.
   *
   * There was a second panel beside it — a list of faults, with a count on its button — and it is
   * gone: every fault it reported is an error on the block's own card, next to the field that
   * fixes it, and the one finding unique to it (contrast) has been retired. What is left is an
   * inspection of how the task is perceived, which has nothing to count.
   */
  panel: PreviewPanel;
  onPanel: (p: PreviewPanel) => void;
  /** Phone/sub-bar: drop the visible select label, keep the control. */
  compact?: boolean;
  /**
   * Offer a way into the Behind the Scenes panel.
   *
   * PHONE ONLY, and the asymmetry is the point: on desktop that panel is simply OPEN — it takes
   * the slot Variables and Flow vacate, and the review panels cover it while they are up — so a
   * button to summon something already on screen would be a fourth control in a row with no room
   * for a third. A phone has nowhere to dock it, so there it needs a door.
   */
}) {
  const { t } = useTranslation();
  const active = 'border-ws-primary bg-ws-btn-active text-ws-btn-active-fg';
  const idle = 'border-ws-border text-ws-text hover:bg-ws-hover';
  return (
    <div className='flex shrink-0 items-center gap-1.5'>
      {/* The task-builder's own dropdown convention rather than a form SelectField: this is a
          toolbar menu, not a field in a block editor. It also portals its popup to <body>, which
          matters here — the bar sits inside overflow-hidden ancestors that would clip a popup.

          FIXED width, not the default `w-fit`: the options differ a lot in length, so a
          fit-to-label trigger resized on every selection and shunted the text-size button beside
          it. Sized so the longest NAME ("Achromatopsia") always fits and only the parenthetical
          gloss truncates — a clipped explanation reads fine, a clipped condition name does not. */}
      <SelectMenu
        value={vision}
        onChange={(v) => onVision(v as VisionMode)}
        ariaLabel={t('task.bottomBar.a11yVision')}
        options={VISION_MODES.map((m) => ({ value: m, label: visionLabel(m, t) }))}
        triggerLabel={visionName(vision, t)}
        fullWidth
        className={compact ? 'w-36 shrink-0' : 'w-44 shrink-0'}
      />
      <button
        type='button'
        onClick={() => onLargeText(!largeText)}
        aria-pressed={largeText}
        aria-label={t('task.bottomBar.a11yLargeText')}
        title={t('task.bottomBar.a11yLargeText')}
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors ${largeText ? active : idle}`}
      >
        {/* An A with an up arrow — the conventional "increase text size" glyph. `ALargeSmall`
            (Aa) reads as a font PICKER rather than a size control. */}
        {/* 20px in a 40px button, for all three of these. At 16px they sat small in their boxes
            next to the vision dropdown, which carries text and so fills its own. */}
        <AArrowUp aria-hidden='true' className='h-5 w-5' />
      </button>
      <button
        type='button'
        onClick={() => onReducedMotion(!reducedMotion)}
        aria-pressed={reducedMotion}
        aria-label={t('task.bottomBar.a11yReducedMotion')}
        title={t('task.bottomBar.a11yReducedMotion')}
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors ${reducedMotion ? active : idle}`}
      >
        {/* Motion, struck through — the same "this is switched off" reading as the muted-speaker
            glyph, and legible at 20px where a snail or a slow-clock is not. */}
        <ZapOff aria-hidden='true' className='h-5 w-5' />
      </button>
      {/* READING — how a screen reader perceives the task. An inspection, not a fault list. */}
      <button
        type='button'
        onClick={() => onPanel(panel === 'reading' ? null : 'reading')}
        aria-pressed={panel === 'reading'}
        aria-label={t('task.bottomBar.a11yReadingHeading')}
        title={t('task.bottomBar.a11yReadingHeading')}
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors ${panel === 'reading' ? active : idle}`}
      >
        {/* An EAR, deliberately. This was `ScanEye`, which put a third eye in a row that already
            has Color vision and Large text beside it and `EyeOff` on Exit — so the one control
            about what a NON-sighted student hears was the one wearing an eye. */}
        <Ear aria-hidden='true' className='h-5 w-5' />
      </button>
    </div>
  );
}
